<template>
  <van-popup
    :show="modelValue"
    position="bottom"
    round
    :safe-area-inset-bottom="mode !== 'country'"
    :class="{ 'area-selector-popup--country': mode === 'country' }"
    :overlay-style="{ background: 'rgba(0, 0, 0, 0.6)' }"
    @update:show="emit('update:modelValue', $event)"
  >
    <section
      class="area-selector"
      :class="{ 'area-selector--country': mode === 'country' }"
    >
      <img
        v-if="mode === 'country'"
        class="area-selector__background"
        :src="backgroundIcon"
        alt=""
      />

      <header
        class="area-selector__header"
        :class="{ 'area-selector__header--country': mode === 'country' }"
      >
        <span class="area-selector__header-spacer" />
        <div data-strong>{{ title }}</div>
        <button type="button" @click="emit('update:modelValue', false)">
          <img
            v-if="mode === 'country'"
            class="app-cancel-icon"
            :src="closeIcon"
            alt=""
          />
          <app-image v-else class="app-cancel-icon" :src="cancelIcon" alt="" />
        </button>
      </header>

      <van-field
        v-if="mode === 'country'"
        v-model="countryKeyword"
        clearable
        :border="false"
        class="area-selector__country-search"
        :placeholder="t('card.searchCountry')"
      />

      <div v-if="mode === 'country'" class="area-selector__country-list">
        <button
          v-for="entry in filteredCountryItems"
          :key="entry.item?.countryId || entry.item?.iso_code_2 || entry.index"
          type="button"
          class="area-selector__country-item"
          @click="selectItem(entry.item, entry.index)"
        >
          <span>{{ displayCountryName(entry.item) }}</span>
          <em>{{ entry.item?.iso_code_2 || "" }}</em>
        </button>

        <van-empty
          v-if="!filteredCountryItems.length"
          image="search"
          :description="t('common.noData')"
        />
      </div>

      <div v-else class="area-selector__body">
        <div ref="scrollRef" class="area-selector__list">
          <template v-for="group in groupedItems" :key="group.letter">
            <div :id="`letter-${group.letter}`" class="area-selector__letter">
              {{ group.letter }}
            </div>
            <button
              v-for="entry in group.items"
              :key="entry.key"
              type="button"
              class="area-selector__area-item"
              @click="selectItem(entry.item, entry.originIndex)"
            >
              {{ displayAreaName(entry.item) }}
            </button>
          </template>
        </div>

        <aside v-if="letters.length" class="area-selector__index">
          <button
            v-for="letter in letters"
            :key="letter"
            type="button"
            :class="{ active: activeLetter === letter }"
            @click="scrollToLetter(letter)"
          >
            {{ letter }}
          </button>
        </aside>
      </div>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getLocalizedAreaName } from "@/utils/display-name";
import backgroundIcon from "@/assets/images/SVG/icon/country-picker-background.svg";
import cancelIcon from "@/assets/images/cancel_gray.png";
import closeIcon from "@/assets/images/SVG/icon/country-picker-close.svg";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  items: {
    type: Array,
    default: () => [],
  },
  mode: {
    type: String,
    default: "country",
  },
});

const emit = defineEmits(["update:modelValue", "select"]);
const { locale, t } = useI18n();

const countryKeyword = ref("");
const scrollRef = ref(null);
const activeLetter = ref("");
let isScrollingByClick = false;

const filteredCountryItems = computed(() => {
  const keyword = String(countryKeyword.value || "")
    .trim()
    .toLocaleLowerCase();

  return props.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => {
      if (!keyword) {
        return true;
      }

      return [
        item?.nameCn,
        item?.nameEn,
        item?.name,
        item?.iso_code_2,
        getLocalizedAreaName(item, "zh-CN"),
        getLocalizedAreaName(item, "en-US"),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(keyword));
    });
});

function areaGroupLetter(item) {
  const nameEn = String(item?.nameEn || "")
    .trim()
    .toUpperCase();
  const letter = nameEn.slice(0, 1);
  return /^[A-Z]$/.test(letter) ? letter : "#";
}

function areaSortName(item) {
  return String(item?.nameEn || "")
    .trim()
    .toUpperCase();
}

const sortedAreaItems = computed(() => {
  return props.items
    .map((item, originIndex) => ({
      item,
      originIndex,
      key: item?.cityId || item?.provinceId || originIndex,
    }))
    .sort((a, b) => {
      const aLetter = areaGroupLetter(a.item);
      const bLetter = areaGroupLetter(b.item);
      if (aLetter !== bLetter) {
        if (aLetter === "#") return 1;
        if (bLetter === "#") return -1;
        return aLetter.localeCompare(bLetter);
      }

      const nameCompare = areaSortName(a.item).localeCompare(
        areaSortName(b.item),
      );
      if (nameCompare !== 0) {
        return nameCompare;
      }

      return String(a.item?.nameCn || "").localeCompare(
        String(b.item?.nameCn || ""),
      );
    });
});

const groupedItems = computed(() => {
  const groups = [];
  for (const entry of sortedAreaItems.value) {
    const letter = areaGroupLetter(entry.item);
    const current = groups[groups.length - 1];
    if (!current || current.letter !== letter) {
      groups.push({ letter, items: [entry] });
    } else {
      current.items.push(entry);
    }
  }
  return groups;
});

const letters = computed(() => groupedItems.value.map((group) => group.letter));

watch(
  () => props.modelValue,
  async (visible) => {
    if (!visible) {
      countryKeyword.value = "";
      return;
    }

    if (props.mode === "country") {
      return;
    }

    await nextTick();
    activeLetter.value = letters.value[0] || "";
    bindScrollListener();
  },
);

watch(
  () => props.items,
  async () => {
    if (!props.modelValue || props.mode === "country") {
      return;
    }

    await nextTick();
    activeLetter.value = letters.value[0] || "";
  },
);

function bindScrollListener() {
  const el = scrollRef.value;
  if (!el || el.dataset.bound === "1") {
    return;
  }

  el.dataset.bound = "1";
  el.addEventListener("scroll", handleScroll, { passive: true });
}

function handleScroll() {
  if (isScrollingByClick) {
    return;
  }

  const container = scrollRef.value;
  if (!container) {
    return;
  }

  let current = letters.value[0] || "";
  for (const letter of letters.value) {
    const section = container.querySelector(`#letter-${letter}`);
    if (!section) {
      continue;
    }

    if (container.scrollTop >= section.offsetTop - 8) {
      current = letter;
    } else {
      break;
    }
  }

  activeLetter.value = current;
}

function scrollToLetter(letter) {
  const container = scrollRef.value;
  const section = container?.querySelector(`#letter-${letter}`);
  if (!container || !section) {
    return;
  }

  isScrollingByClick = true;
  activeLetter.value = letter;
  container.scrollTo({
    top: section.offsetTop,
    behavior: "smooth",
  });
  setTimeout(() => {
    isScrollingByClick = false;
  }, 300);
}

function selectItem(item, index) {
  emit("select", { item, index });
  emit("update:modelValue", false);
}

function displayCountryName(item) {
  return getLocalizedAreaName(item, locale.value) || "--";
}

function displayAreaName(item) {
  return getLocalizedAreaName(item, locale.value) || "--";
}

onBeforeUnmount(() => {
  const el = scrollRef.value;
  if (el) {
    el.removeEventListener("scroll", handleScroll);
  }
});
</script>

<style scoped lang="scss">
:deep(.area-selector-popup--country) {
  border-radius: 20px 20px 0 0 !important;
}

.area-selector {
  display: flex;
  height: 60vh;
  flex-direction: column;
  background: #ffffff;
}

.area-selector--country {
  position: relative;
  height: 400px;
  max-height: 80vh;
  overflow: hidden;
  background: transparent;
}

.area-selector__background {
  position: absolute;
  z-index: 0;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
}

.area-selector__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px;
  border-bottom: 1px solid #f5f5f5;

  [data-strong] {
    color: #1f2430;
    font-size: 18px;
    font-weight: 700;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    background: transparent;

    .app-image {
      display: block;
      width: 18px;
      height: 18px;
    }
  }
}

.area-selector__header--country {
  position: absolute;
  z-index: 1;
  top: 20px;
  right: 0;
  left: 0;
  height: 22px;
  justify-content: center;
  padding: 0 16px;
  border-bottom: 0;

  [data-strong] {
    color: #000000;
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;
  }

  button {
    position: absolute;
    top: 4px;
    right: 16px;
    width: 16px;
    height: 16px;

    .app-image {
      width: 16px;
      height: 16px;
    }

    .app-cancel-icon {
      display: block;
      width: 16px;
      height: 16px;
    }
  }
}

.area-selector__header-spacer {
  width: 18px;
  height: 18px;
}

.area-selector__header--country .area-selector__header-spacer {
  display: none;
}

:deep(.area-selector__country-search) {
  position: absolute;
  z-index: 1;
  top: 62px;
  left: 16px;
  box-sizing: border-box;
  width: calc(100% - 32px);
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 8px 16px;
  border: 1px solid #dce0e7;
  border-radius: 10px;
  background: #ffffff;
}

:deep(.area-selector__country-search .van-field__control),
:deep(.area-selector__country-search .van-field__control::placeholder) {
  color: #666666;
  font-size: 14px;
  line-height: 18px;
}

:deep(.area-selector__country-search .van-field__clear) {
  margin-right: -4px;
  padding: 0 4px;
  color: #999999;
  font-size: 14px;
  line-height: 14px;
}

:deep(.area-selector__country-search .van-icon-clear) {
  font-size: 14px;
}

.area-selector__country-list {
  position: absolute;
  z-index: 1;
  top: 112px;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-y: auto;
}

.area-selector__country-item,
.area-selector__area-item {
  display: flex;
  width: 100%;
  min-height: 56px;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border: 0;
  border-bottom: 1px solid #f5f5f5;
  background: #ffffff;
  color: #333333;
  text-align: left;
  font-size: 16px;
}

.area-selector__country-item {
  box-sizing: border-box;
  height: 48px;
  min-height: 48px;
  line-height: 16px;
}

.area-selector__country-item em {
  color: #999999;
  font-style: normal;
}

.area-selector__body {
  display: flex;
  min-height: 0;
  flex: 1;
}

.area-selector__list {
  flex: 1;
  overflow-y: auto;
}

.area-selector__letter {
  display: flex;
  height: 32px;
  align-items: center;
  padding: 0 16px;
  border-top: 1px solid #f5f5f5;
  color: #666666;
  font-size: 14px;
}

.area-selector__index {
  display: flex;
  width: 32px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  background: #ffffff;

  button {
    display: flex;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    margin: 1px 0;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: #666666;
    font-size: 12px;

    &.active {
      background: var(--wp-color-brand-1);
      color: #ffffff;
    }
  }
}
</style>
